<template>
  <div
    class="tag"
    :class="[themeClass, color, sizeClass]"
    tabindex="1"
    :data-cy="dataCy"
    @focus="onFocus"
    @keydown="onKeydown"
    @blur="onBlur"
    @mousedown="onMousedown"
  >
    <div class="body">
      <inline-svg v-if="resolvedIcon" class="tag-icon" :src="resolvedIcon" />
      <div>{{ text }}</div>

      <slot name="right">
        <OurSvgIcon
          v-if="tooltipText"
          :tooltip="tooltipText"
          :tooltip-settings="tooltipSettings"
          :data-cy="`${dataCy}-info`"
          :src="icons.InfoCircle"
          :color="iconColor || 'gray'"
          variant="light"
          size="xs"
        />
      </slot>
    </div>
  </div>
</template>

<script>
import InlineSvg from "vue-inline-svg";
import { getThemeClass } from "@/services/_bit/UiService";

import OurSvgIcon from "@/components/_bit/IconSvg";

export default {
  name: "OurTag",
  components: {
    InlineSvg,
    OurSvgIcon,
  },

  props: {
    /**
     * The color of the tag.
     * @values gray, red, orange, yellow, green, blue, violet, fuchsia, earlyBird
     */
    color: {
      type: String,
      default: "blue",
    },

    /**
     * Optional left icon source. Early bird tags get a default chick icon.
     */
    icon: {
      type: String,
      default: "",
    },

    /**
     * The size of the tag.
     * @values sm, md, lg
     */
    size: {
      type: String,
      default: "md",
    },

    /**
     * Set tag text.
     */
    text: {
      type: String,
      required: true,
    },

    /**
     * Set tooltip text.
     */
    tooltipText: {
      type: String,
      default: "",
    },

    tooltipSettings: {
      type: Object,
      default: () => {},
    },

    /**
     * The color of the tooltip icon.
     * @values accent, gray, red, yellow, green, blue, violet, black, white
     */
    iconColor: {
      type: String,
      default: "",
    },

    /**
     * Sets data-cy attribute for correct element sampling in tests.
     */
    dataCy: {
      type: String,
      default: "",
    },
  },

  computed: {
    themeClass() {
      return getThemeClass();
    },

    icons: () => ({
      InfoCircle: require("@/components/_bit/IconSvg/icons/Info-circle.svg"),
    }),

    sizeClass() {
      return `size-${this.size}`;
    },

    resolvedIcon() {
      if (this.icon) {
        return this.icon;
      }

      if (this.color === "earlyBird") {
        return require("@/components/_bit/IconSvg/icons/Early-bird.svg");
      }

      return "";
    },
  },

  methods: {
    onFocus() {
      this.$emit("focus");
    },

    onKeydown() {
      this.$emit("keydown");
    },

    onBlur(event) {
      this.$emit("blur", event);
    },

    onMousedown() {
      this.$emit("mousedown");
    },
  },
};
</script>

<style lang="postcss" scoped>
.tag {
  @apply rounded-full;
  @apply flex items-center whitespace-nowrap;

  .body {
    @apply flex items-center gap-1;
  }

  .tag-icon {
    @apply shrink-0 object-contain;
  }
}

.size-sm {
  @apply p-0.5 px-2;
  @apply text-2xs;

  .tag-icon {
    @apply h-3 w-3;
  }
}

.size-md {
  @apply px-2 py-1;
  @apply text-xs;

  .tag-icon {
    @apply h-3.5 w-3.5;
  }
}

.size-lg {
  @apply px-3 py-1.5;
  @apply text-sm;

  .tag-icon {
    @apply h-6 w-6;
  }
}

.gray {
  @apply bg-gray-100 text-gray-900;
}

.red {
  @apply bg-red-100 text-red-800;
}

.orange {
  @apply bg-orange-100 text-orange-800;
}

.yellow {
  @apply bg-yellow-100 text-yellow-800;
}

.green {
  @apply bg-green-100 text-green-800;
}

.blue {
  @apply bg-blue-100 text-blue-800;
}

.violet {
  @apply bg-violet-100 text-violet-800;
}

.fuchsia {
  @apply bg-fuchsia-100 text-fuchsia-800;
}

.transparent {
  @apply bg-transparent text-fuchsia-800;
}

.earlyBird {
  background-color: #2a2205;
  @apply px-2.5 py-1 text-xs font-medium text-white transition-colors duration-150;

  .tag-icon {
    @apply h-4 w-4 overflow-visible;
    transform-origin: 50% 80%;
    animation: early-bird-idle 2.2s ease-in-out infinite;
  }

  &:hover,
  &:focus {
    background-color: #f5c542;
    @apply text-black outline-none;

    .tag-icon {
      animation: early-bird-hop 0.55s ease-in-out infinite;
    }

    :deep(.early-bird__tail) {
      transform-box: fill-box;
      transform-origin: right center;
      animation: early-bird-tail 0.55s ease-in-out infinite;
    }

    :deep(.early-bird__beak) {
      transform-box: fill-box;
      transform-origin: left center;
      animation: early-bird-peck 0.55s ease-in-out infinite;
    }

    :deep(.tag-icon [fill="#FFC93C"]),
    :deep(.tag-icon [fill="#F2A900"]) {
      fill: #ffffff;
    }
  }
}

@keyframes early-bird-idle {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }

  50% {
    transform: translateY(-1px) rotate(-6deg);
  }
}

@keyframes early-bird-hop {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }

  25% {
    transform: translateY(-2px) rotate(-14deg);
  }

  50% {
    transform: translateY(0) rotate(8deg);
  }

  75% {
    transform: translateY(-1.5px) rotate(-8deg);
  }
}

@keyframes early-bird-tail {
  0%,
  100% {
    transform: rotate(0deg);
  }

  50% {
    transform: rotate(-18deg);
  }
}

@keyframes early-bird-peck {
  0%,
  100% {
    transform: rotate(0deg);
  }

  40% {
    transform: rotate(16deg);
  }

  70% {
    transform: rotate(-8deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .earlyBird .tag-icon,
  .earlyBird:hover :deep(.early-bird__tail),
  .earlyBird:hover :deep(.early-bird__beak) {
    animation: none;
  }
}
</style>
